/**
 * Signing the packet at the desk.
 *
 * Four steps, in an order the law cares about:
 *
 *   1. The dealer confirms whose licence he is looking at. This happens before
 *      anything is shown to the customer, because it is the identity proofing
 *      the whole thing rests on.
 *   2. The customer reads the electronic-records disclosure and agrees. ESIGN
 *      requires consent BEFORE the records are delivered electronically, not
 *      as a checkbox at the end.
 *   3. Each person signs once. The mark is applied everywhere their signature
 *      belongs, the way adopting a signature works on paper.
 *   4. The packet is sealed: signatures stamped in, certificate appended,
 *      hashed, filed.
 *
 * The customer can stop at any point and the dealer prints instead. That is
 * not a courtesy, it is the condition on which the consent was given.
 */

import { useEffect, useState } from "react";
import { Button, ErrorNote, Panel } from "../../components/ui";
import { TextField } from "../../components/fields";
import SignaturePad from "../../components/SignaturePad";
import { openForPrint } from "./buildPacket";
import { ROLE_LABEL, rolesNeeded, type SignerRole } from "../../lib/signing/anchors";
import {
  startInPerson, recordConsent, saveSignature, sealPacket, loadSignatures,
  declineSigning, logEvent, outstanding, ESIGN_DISCLOSURE,
  type SigningSession, type StoredSignature,
} from "./signing";

type Step = "identity" | "consent" | "sign" | "done";

export default function SignPacket({
  dealId, dealNo, docKeys, buyerName, coBuyerName, dealerName, idOnFile,
  resume, onClose, onSealed,
}: {
  dealId: string;
  dealNo: string;
  docKeys: string[];
  buyerName: string;
  coBuyerName: string | null;
  dealerName: string;
  /** What the customer record says, so the dealer is checking rather than typing. */
  idOnFile: { type: string; state: string; number: string; expiration: string | null };
  /**
   * An existing session to carry on with, rather than starting a new one.
   *
   * This is how a remote signing gets finished. The customer has already
   * proved who she is the way that session allows and has already consented —
   * both are recorded against that session and both happened before she
   * signed. Starting a fresh session here would throw her signature away and
   * re-date the consent to after the fact, which is the one thing the
   * certificate must never claim. So the first two steps are skipped: they
   * have happened, and not to the dealer.
   */
  resume?: SigningSession | null;
  onClose: () => void;
  onSealed: (bytes: Uint8Array, sha256: string) => void;
}) {
  const [step, setStep] = useState<Step>(resume ? "sign" : "identity");
  const [session, setSession] = useState<SigningSession | null>(resume ?? null);
  const [signed, setSigned] = useState<StoredSignature[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [staffName, setStaffName] = useState("");
  const [idMatches, setIdMatches] = useState(false);
  const [photoMatches, setPhotoMatches] = useState(false);
  const [notExpired, setNotExpired] = useState(false);

  const [current, setCurrent] = useState<SignerRole | null>(null);
  /**
   * True until a resumed session's existing signatures have been read.
   *
   * Without this there is a window, between mount and that read landing, where
   * `signed` is still empty — so the outstanding list looks like nobody has
   * signed and the screen offers the BUYER's pad to whoever is holding the
   * device. The dealer is holding it, and a signature saved then is upserted
   * on (session_id, role), which overwrites the customer's real signature with
   * the dealer's. Nothing is drawn until we know who has already signed.
   */
  const [hydrating, setHydrating] = useState(Boolean(resume));
  const [drawn, setDrawn] = useState<string | null>(null);
  const [initials, setInitials] = useState<string | null>(null);

  const roles = rolesNeeded(docKeys, Boolean(coBuyerName));
  const nameFor = (r: SignerRole) =>
    r === "buyer" ? buyerName : r === "co_buyer" ? (coBuyerName ?? "") : dealerName;

  const left = session ? outstanding(docKeys, Boolean(coBuyerName), signed) : roles;

  useEffect(() => {
    if (hydrating) return;
    if (step === "sign" && !current && left.length) setCurrent(left[0]!);
  }, [hydrating, step, current, left]);

  /* A resumed session already has marks in it. Load them before deciding who
     is left, or the customer gets asked to sign again on the dealer's screen. */
  useEffect(() => {
    if (!resume) return;
    let live = true;
    loadSignatures(resume.id)
      .then((got) => {
        if (!live) return;
        setSigned(got);
        const remaining = outstanding(docKeys, Boolean(coBuyerName), got);
        setCurrent(remaining[0] ?? null);
        if (remaining.length === 0) setStep("done");
        setHydrating(false);
      })
      .catch((e: Error) => {
        if (!live) return;
        /* Stay hydrating on failure. A pad shown here would be a pad over an
           unknown signature state, which is the thing this guards against. */
        setErr(`${e.message} — reopen the deal and try again.`);
      });
    return () => { live = false; };
  }, [resume, docKeys, coBuyerName]);

  const expired = idOnFile.expiration
    ? idOnFile.expiration < new Date().toISOString().slice(0, 10)
    : false;

  /* ---- step 1: the dealer attests --------------------------------- */
  const beginSigning = async () => {
    if (!staffName.trim()) { setErr("Enter your name — the certificate records who checked the ID."); return; }
    setBusy(true); setErr(null);
    try {
      const s = await startInPerson(dealId, docKeys, {
        idType: idOnFile.type,
        idState: idOnFile.state,
        idLast4: idOnFile.number.replace(/\W/g, "").slice(-4),
        idExpiration: idOnFile.expiration,
        attestedByName: staffName.trim(),
      });
      setSession(s);
      setStep("consent");
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  /* ---- step 2: the customer consents ------------------------------ */
  const agree = async () => {
    if (!session) return;
    setBusy(true); setErr(null);
    try {
      await recordConsent(session.id, buyerName);
      setStep("sign");
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const refuse = async () => {
    if (!session) return;
    setBusy(true);
    try {
      await declineSigning(session.id, buyerName, "Customer chose to sign on paper");
      onClose();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  /* ---- step 3: each person signs ---------------------------------- */
  const adopt = async () => {
    if (!session || !current || !drawn) return;
    setBusy(true); setErr(null);
    try {
      await saveSignature(session.id, current, nameFor(current), drawn, initials);
      const next = await loadSignatures(session.id);
      setSigned(next);
      setDrawn(null);
      setInitials(null);
      const remaining = outstanding(docKeys, Boolean(coBuyerName), next);
      setCurrent(remaining[0] ?? null);
      if (remaining.length === 0) setStep("done");
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  /* ---- step 4: seal ------------------------------------------------ */
  const seal = async () => {
    if (!session) return;
    setBusy(true); setErr(null);
    try {
      const sealed = await sealPacket(session.id, { attestedByName: staffName });
      openForPrint(sealed.bytes, `${dealNo}-signed.pdf`);
      onSealed(sealed.bytes, sealed.sha256);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  /* ================================================================= */

  return (
    <Panel title={`Sign the packet — ${dealNo}`} rail="keep">
      {err && <ErrorNote>{err}</ErrorNote>}

      <ol className="steps">
        {(["identity", "consent", "sign", "done"] as Step[]).map((s, i) => (
          <li key={s} className={step === s ? "on" :
            (["identity", "consent", "sign", "done"] as Step[]).indexOf(step) > i ? "past" : ""}>
            {["Check ID", "Consent", "Signatures", "Finish"][i]}
          </li>
        ))}
      </ol>

      {/* ---- 1. identity ---- */}
      {step === "identity" && (
        <>
          <p className="hint">
            Florida lets the title paperwork be signed electronically, but only
            where the signer's identity has been properly established. In person
            that means you looking at their photo ID before anything is signed.
            What you confirm here is what the certificate will say.
          </p>

          <div className="idcard">
            <div className="li"><span>On file</span>
              <span>{idOnFile.type}{idOnFile.state ? ` — ${idOnFile.state}` : ""}</span></div>
            <div className="li"><span>Number</span>
              <span className="mono">{idOnFile.number || "—"}</span></div>
            <div className="li"><span>Expires</span>
              <span className={expired ? "flag late" : ""}>
                {idOnFile.expiration ?? "not recorded"}
              </span></div>
          </div>

          {expired && (
            <ErrorNote>
              That ID has expired. An expired licence is not acceptable identity
              proofing — get a current one and update the customer record before
              signing electronically.
            </ErrorNote>
          )}

          <div className="attest">
            <label className="checkline">
              <input type="checkbox" checked={idMatches}
                     onChange={(e) => setIdMatches(e.target.checked)} />
              I have the physical ID in my hand and the number matches the record above
            </label>
            <label className="checkline">
              <input type="checkbox" checked={photoMatches}
                     onChange={(e) => setPhotoMatches(e.target.checked)} />
              The photograph and name match the person in front of me
            </label>
            <label className="checkline">
              <input type="checkbox" checked={notExpired}
                     onChange={(e) => setNotExpired(e.target.checked)} />
              The ID is current and not expired
            </label>
          </div>

          <TextField label="Your name" value={staffName} onChange={setStaffName}
                     hint="Recorded on the certificate as the person who verified the ID" />

          <div className="actions">
            <Button disabled={busy || expired || !idMatches || !photoMatches || !notExpired}
                    onClick={beginSigning}>
              {busy ? "Starting…" : "Start signing"}
            </Button>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </>
      )}

      {/* ---- 2. consent ---- */}
      {step === "consent" && (
        <>
          <p className="hint">
            Hand the tablet to {buyerName}. This has to be read and agreed to
            before anything is signed.
          </p>
          <div className="disclosure">{ESIGN_DISCLOSURE}</div>
          <div className="actions">
            <Button disabled={busy} onClick={agree}>
              {busy ? "Recording…" : "I agree — continue"}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={refuse}>
              I would rather sign on paper
            </Button>
          </div>
        </>
      )}

      {hydrating && (
        <p className="hint">Checking who has already signed…</p>
      )}

      {/* ---- 3. signatures ---- */}
      {step === "sign" && current && !hydrating && (
        <>
          {resume && resume.mode === "remote" && signed.length > 0 && (
            <p className="taxnote">
              {signed.map((x) => `${nameFor(x.role)} signed `
                + `${new Date(x.signed_at).toLocaleString()}`).join("; ")}
              {" "}from the link. Their consent and identity check are already on
              the certificate — you are only adding the dealership signature.
            </p>
          )}
          <div className="signwho">
            <span className="flag">{ROLE_LABEL[current]}</span>
            <strong>{nameFor(current)}</strong>
          </div>
          <p className="hint">
            Sign once. The same signature is applied everywhere {" "}
            {current === "dealer" ? "the dealership signs" : "this person signs"} across
            all {docKeys.length} documents.
          </p>

          <SignaturePad label="Full signature" onChange={setDrawn} />
          <SignaturePad label="Initials — used on the as-is and arbitration lines"
                        height={120} onChange={setInitials} />

          <div className="actions">
            <Button disabled={busy || !drawn} onClick={adopt}>
              {busy ? "Saving…" : `Adopt ${nameFor(current)}'s signature`}
            </Button>
            {left.length > 1 && (
              <span className="hint">
                Then {left.slice(1).map((r) => nameFor(r)).join(", ")}.
              </span>
            )}
          </div>

          {signed.length > 0 && (
            <p className="taxnote">
              Signed so far: {signed.map((s) => nameFor(s.role)).join(", ")}.
            </p>
          )}
        </>
      )}

      {/* ---- 4. seal ---- */}
      {step === "done" && (
        <>
          <p className="hint">
            Everyone has signed. Sealing applies the signatures to all{" "}
            {docKeys.length} documents, appends the certificate of electronic
            signature with the full audit trail, and files a hashed copy.
          </p>
          <div className="item">
            {signed.map((s) => (
              <div className="li" key={s.role}>
                <span>{nameFor(s.role)}<span className="flag ok">signed</span></span>
                <span className="hint">{new Date(s.signed_at).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
          <div className="actions">
            <Button disabled={busy} onClick={seal}>
              {busy ? "Sealing…" : "Seal and print"}
            </Button>
            <Button variant="ghost" disabled={busy}
                    onClick={() => { if (session) void logEvent(session.id, "sealing_deferred"); onClose(); }}>
              Not yet
            </Button>
          </div>
        </>
      )}
    </Panel>
  );
}
